import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api } from './api';

export interface AdminIdentity {
  admin: { id: string; email: string; name: string };
  environment: string;
  display_timezone: string;
  mfa_enrolled: boolean;
  mfa_satisfied: boolean;
  reauth_valid_until: string | null;
  roles: { role: string; game_id: string | null; environment: string | null }[];
  visible_game_ids: string[] | null;
  permissions: string[];
  can_receive_coupon_codes?: boolean;
}

export interface GameSummary {
  id: string;
  slug: string;
  name: string;
  platforms: string[];
  lifecycle: string;
  status: string;
}

interface AdminSessionValue {
  identity: AdminIdentity | null;
  loading: boolean;
  error: ApiError | null;
  games: GameSummary[];
  /** null = portfolio (all games) */
  scopeGameId: string | null;
  setScopeGameId: (gameId: string | null) => void;
  scopeGame: GameSummary | null;
  refresh: () => Promise<void>;
  can: (permission: string) => boolean;
  signOut: () => Promise<void>;
}

const AdminSessionContext = createContext<AdminSessionValue | null>(null);
const SCOPE_STORAGE_KEY = 'askrs.admin.scope';

export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<AdminIdentity | null>(null);
  const [games, setGames] = useState<GameSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  // Scope survives navigation and reloads until it is explicitly changed.
  const [scopeGameId, setScopeGameIdState] = useState<string | null>(
    () => window.localStorage.getItem(SCOPE_STORAGE_KEY) || null,
  );

  const setScopeGameId = useCallback((gameId: string | null) => {
    setScopeGameIdState(gameId);
    if (gameId) window.localStorage.setItem(SCOPE_STORAGE_KEY, gameId);
    else window.localStorage.removeItem(SCOPE_STORAGE_KEY);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const me = await api.admin<AdminIdentity>('/me');
      setIdentity(me.data);

      const gameList = await api.admin<GameSummary[]>('/games', { query: { per_page: 200 } });
      const allGames = [...gameList.data];
      for (let page = 2; page <= (gameList.meta.total_pages ?? 1); page += 1) {
        const next = await api.admin<GameSummary[]>('/games', { query: { per_page: 200, page } });
        allGames.push(...next.data);
      }
      setGames(allGames);
    } catch (cause) {
      setIdentity(null);
      setError(cause instanceof ApiError ? cause : null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.admin('/auth/logout', { method: 'POST' });
    } finally {
      setIdentity(null);
      window.location.assign('/admin/sign-in');
    }
  }, []);

  const value = useMemo<AdminSessionValue>(() => ({
    identity,
    loading,
    error,
    games,
    scopeGameId,
    setScopeGameId,
    scopeGame: games.find((game) => game.id === scopeGameId) ?? null,
    refresh,
    can: (permission: string) => Boolean(identity?.permissions.includes(permission)),
    signOut,
  }), [identity, loading, error, games, scopeGameId, setScopeGameId, refresh, signOut]);

  return <AdminSessionContext.Provider value={value}>{children}</AdminSessionContext.Provider>;
}

export function useAdminSession(): AdminSessionValue {
  const context = useContext(AdminSessionContext);
  if (!context) throw new Error('useAdminSession must be used inside AdminSessionProvider');
  return context;
}
